<template>
  <main class="kanban-v2" id="js-page-content" role="main">
    <portal to="ariane">
      <div class="tbv2-page-title">{{ $t('post.page.kanban.title') || 'Kanban' }}</div>
    </portal>

    <header class="kanban-hero">
      <div class="kanban-hero-text">
        <h1 class="kanban-hero-title">{{ $t('post.page.kanban.title') || 'Kanban' }}</h1>
        <p class="kanban-hero-sub" v-if="selectedForm">
          {{ selectedForm.name }}
        </p>
        <p class="kanban-hero-sub" v-else>
          {{ $t('post.page.kanban.subtitle') || 'Sélectionnez un formulaire pour afficher son tableau.' }}
        </p>
      </div>
      <MultipleListCreate class="kanban-multi-list" />
    </header>

    <!-- Form Selection -->
    <KanbanFormSelector v-if="!selectedForm" :formTypeId="formTypeId" @select="selectForm" />

    <!-- Kanban Board -->
    <template v-else>
      <div class="kanban-toolbar">
        <button type="button" class="kb-back-btn" @click="backToSelection">
          <i class="fal fa-arrow-left"></i>
          <span>{{ $t('post.page.kanban.back_to_selection') }}</span>
        </button>
      </div>

      <section class="kanban-card">
        <PostsFilters v-if="!loading" :singleForm="selectedForm" :formTypeId="formTypeId" @form-changed="onFormChanged" />
      </section>

      <div v-if="loading" class="kanban-loading">
        <i class="fal fa-spinner-third fa-spin"></i>
        <span>{{ $t('forms.loading') }}...</span>
      </div>

      <BmvKanban
        v-else
        :columns="kanbanColumns"
        @move="onMove"
      >
        <template #card="{ item, column }">
          <PanelKanban :post="item" :columnStatus="column" />
        </template>
      </BmvKanban>
    </template>
  </main>
</template>

<script>
import PanelKanban from '../../components/UI/PanelKanban'
import BmvKanban from '@/components/UI/BmvKanban'
import MultipleListCreate from '@/components/UI/MultipleListCreate'
import KanbanFormSelector from '@/components/UI/KanbanFormSelector'
import { mapActions, mapState } from 'vuex'
import PostsFilters from '@/components/Model/Post/PostsFilters'

export default {
  name: 'Kanban',
  components: {
    PanelKanban,
    BmvKanban,
    MultipleListCreate,
    KanbanFormSelector,
    PostsFilters
  },
  data() {
    return {
      loading: true,
      attributes: {},
      isLoadingStatus: false,
      selectedForm: null,
      statusIndexClicked: ''
    }
  },
  computed: {
    ...mapState('auth', {
      user: (state) => state.user
    }),
    ...mapState('post', {
      posts: (state) => state.posts
    }),
    ...mapState('form', {
      allForms: (state) => state.forms
    }),
    formTypeId() {
      return this.$route.params.form_type_id || null
    },
    selectedFormStatuses() {
      if (!this.selectedForm || !this.allForms) return []
      const form = this.allForms.find((f) => f.id === this.selectedForm.id)
      return form && form.statuses ? form.statuses : []
    },
    config: {
      get() {
        return this.$store.state.post.postsConfig
      },
      set(value) {
        this.$store.commit('post/setPostsConfig', value)
      }
    },
    kanbanColumns() {
      if (!this.selectedForm || !this.attributes[this.selectedForm.id]) return []
      const formAttrs = this.attributes[this.selectedForm.id]
      return Object.keys(formAttrs).map((statusId) => {
        const col = formAttrs[statusId]
        return {
          id: col.status.id,
          title: col.status.name,
          icon: col.status.icon,
          items: col.posts
        }
      })
    }
  },
  watch: {
    posts: {
      handler() {
        if (!this.selectedForm) return
        this.buildColumns()
      }
    }
  },
  methods: {
    ...mapActions('post', ['getPosts', 'updateStatus']),
    selectForm(form) {
      this.selectedForm = form
      this.doGetStatuses()
    },
    onFormChanged(form) {
      if (form && form.id !== this.selectedForm.id) {
        this.selectForm(form)
      }
    },
    backToSelection() {
      this.selectedForm = null
      this.loading = true
      this.attributes = {}
    },
    buildColumns() {
      const formId = this.selectedForm.id
      this.loading = true

      const formAttrs = {}
      let defaultStatusId = null
      this.selectedFormStatuses.forEach((status) => {
        formAttrs[status.id] = { status, posts: [] }
        if (status.default && defaultStatusId === null) defaultStatusId = status.id
      })
      if (defaultStatusId === null && this.selectedFormStatuses.length > 0) {
        defaultStatusId = this.selectedFormStatuses[0].id
      }

      // Build a name→id index for fuzzy matching when IDs don't align (data inconsistency)
      const statusByName = {}
      this.selectedFormStatuses.forEach((s) => {
        statusByName[s.name.trim().toLowerCase()] = s.id
      })

      if (this.posts && this.posts.data) {
        this.posts.data.forEach((post) => {
          let targetId = null
          if (formAttrs[post.form_status_id]) {
            targetId = post.form_status_id
          } else if (post.status && post.status.name) {
            targetId = statusByName[post.status.name.trim().toLowerCase()] || null
          }
          if (targetId === null) targetId = defaultStatusId
          if (targetId !== null && formAttrs[targetId]) {
            formAttrs[targetId].posts.push(post)
          }
        })
      }

      this.$set(this.attributes, formId, formAttrs)
      this.loading = false
    },
    onMove({ sourceColIndex, sourceItemIndex, targetColIndex }) {
      if (this.isLoadingStatus) return

      const sourceCol = this.kanbanColumns[sourceColIndex]
      const targetCol = this.kanbanColumns[targetColIndex]
      const post = sourceCol.items[sourceItemIndex]
      const targetStatus = targetCol

      this.isLoadingStatus = true
      this.updateStatus({
        id: post.id,
        form_status_id: targetStatus.id
      }).then(() => {
        post.form_status_id = targetStatus.id
        post.status = { id: targetStatus.id, name: targetStatus.title, icon: targetStatus.icon }

        // Move post between columns
        const formId = this.selectedForm.id
        this.attributes[formId][sourceCol.id].posts.splice(sourceItemIndex, 1)
        this.attributes[formId][targetCol.id].posts.push(post)
        this.attributes = JSON.parse(JSON.stringify(this.attributes))

        this.isLoadingStatus = false
      })
    },
    doGetStatuses() {
      this.config.include = 'medias,form,status,company,createdBy,answers'
      this.config.filters = { form_id: this.selectedForm.id }
      this.getPosts()
        .then(() => {
          this.buildColumns()
        })
        .catch((error) => {
          this.$store.dispatch('dispatchError', error)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.kanban-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.kanban-hero {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--v2-space-4);
    margin-bottom: var(--v2-space-5);
}

.kanban-hero-text {
    min-width: 0;
}

.kanban-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.kanban-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
}

.kanban-multi-list {
    flex-shrink: 0;
}

.kanban-toolbar {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    margin-bottom: var(--v2-space-3);
}

.kb-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    background: var(--v2-surface-0);
    color: var(--v2-text-2);
    font-family: inherit;
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-medium);
    line-height: 1.2;
    cursor: pointer;
    box-sizing: border-box;
    transition: background var(--v2-transition-fast), border-color var(--v2-transition-fast), color var(--v2-transition-fast);
}

.kb-back-btn i {
    font-size: 0.78rem;
}

.kb-back-btn:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.kanban-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: visible;
    margin-bottom: var(--v2-space-4);
}

.kanban-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-2);
    padding: var(--v2-space-8, 48px) var(--v2-space-5);
    color: var(--v2-text-3);
    font-size: var(--v2-text-sm-size);
}

.kanban-loading i {
    font-size: 1rem;
    color: var(--v2-primary);
}

@media (max-width: 900px) {
    .kanban-v2 {
        padding: 16px 18px 32px;
    }

    .kanban-hero {
        flex-direction: column;
        align-items: stretch;
        gap: var(--v2-space-3);
    }

    .kanban-hero-title {
        font-size: 1.375rem;
    }

    .kb-back-btn span {
        display: none;
    }

    .kb-back-btn {
        width: 32px;
        padding: 0;
        justify-content: center;
    }
}
</style>
